import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart, Bar, Cell, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ResponsiveContainer,
} from "recharts";
import { humanEUR } from "../blocks/format";

// Finance-grade palette (SP1 approved mockup): actual months use the saturated brand tones;
// forecast months use a lighter tint of the same hue so a reader can tell "projected" from
// "booked" at a glance, without a second legend or interactive tooltip to explain it.
export const CLOUD_COLOR = "#3b5ba5";
export const AI_COLOR = "#0e8a7d";
export const BUDGET_COLOR = "#c0392b";
export const CLOUD_FORECAST_COLOR = "#9db0d6";
export const AI_FORECAST_COLOR = "#8fcfc7";
const GRID_COLOR = "#e5e7eb";
const AXIS_COLOR = "#6b6b6b";

// firstForecastMonth - pure helper: the month label of the first is_forecast point, used to
// place the actual/forecast ReferenceLine divider. Returns null when there is no forecast
// segment, or when every point is a forecast (idx 0 - there's no "actual" side to mark the
// edge of, so a divider would be meaningless).
export function firstForecastMonth(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  const idx = points.findIndex((p) => p && p.is_forecast);
  if (idx <= 0) return null;
  return points[idx].month;
}

const LEGEND_ITEMS = [
  { key: "cloud", label: "Cloud", color: CLOUD_COLOR },
  { key: "ai", label: "AI", color: AI_COLOR },
  { key: "budget", label: "Budget", color: BUDGET_COLOR },
  { key: "forecast", label: "Forecast", color: CLOUD_FORECAST_COLOR },
];

// StaticLegend - plain HTML swatches, not recharts <Legend>. Recharts' Legend defaults to a
// click-to-toggle-series control, which is dead (and confusing) chrome on a page that only
// ever renders once into a headless-Chrome PDF. A static row communicates the same mapping.
function StaticLegend() {
  return (
    <div
      className="stacked-trend-chart-legend"
      role="list"
      aria-label="Chart legend"
      style={{
        display: "flex", gap: "16px", justifyContent: "center", marginTop: "6px",
        fontSize: "9pt", color: AXIS_COLOR, fontFamily: "Georgia, 'Times New Roman', serif",
      }}
    >
      {LEGEND_ITEMS.map((item) => (
        <span key={item.key} role="listitem" style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <span
            aria-hidden="true"
            style={{ width: "10px", height: "10px", borderRadius: "2px", background: item.color, display: "inline-block" }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/**
 * StackedTrendChart - print-static Cloud+AI stacked spend trend vs. budget, for the SP1 board
 * report (Task-1 GET /figures/trend -> { trend: { points: [{ month, cloud, ai, budget,
 * is_forecast }] } }).
 *
 * Print-static: no Tooltip, no interactive Legend, no hover/zoom - this renders once into a
 * headless-Chrome PDF, where none of that chrome would ever be seen or used. A static legend
 * row stands in for the interactive one.
 */
export default function StackedTrendChart({ points = [] }) {
  if (!Array.isArray(points) || points.length === 0) {
    return (
      <div
        className="stacked-trend-chart-empty"
        style={{ fontSize: "9pt", color: AXIS_COLOR, fontStyle: "italic", padding: "8px 0" }}
      >
        No trend data available.
      </div>
    );
  }

  const divider = firstForecastMonth(points);

  return (
    <div className="stacked-trend-chart" style={{ width: "100%" }}>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={points} margin={{ top: 16, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis
              dataKey="month"
              stroke={AXIS_COLOR}
              tick={{ fontSize: 9, fill: AXIS_COLOR }}
              tickLine={false}
              axisLine={{ stroke: GRID_COLOR }}
            />
            <YAxis
              stroke={AXIS_COLOR}
              tick={{ fontSize: 9, fill: AXIS_COLOR }}
              tickFormatter={(v) => humanEUR(v)}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            {divider != null && (
              <ReferenceLine
                x={divider}
                stroke={AXIS_COLOR}
                strokeDasharray="3 3"
                label={{ value: "forecast →", position: "top", fontSize: 8, fill: AXIS_COLOR }}
              />
            )}
            <Bar dataKey="cloud" stackId="spend" name="Cloud" isAnimationActive={false}>
              {points.map((p, i) => (
                <Cell key={`cloud-${p?.month ?? i}`} fill={p?.is_forecast ? CLOUD_FORECAST_COLOR : CLOUD_COLOR} />
              ))}
            </Bar>
            <Bar dataKey="ai" stackId="spend" name="AI" isAnimationActive={false}>
              {points.map((p, i) => (
                <Cell key={`ai-${p?.month ?? i}`} fill={p?.is_forecast ? AI_FORECAST_COLOR : AI_COLOR} />
              ))}
            </Bar>
            <Line
              type="monotone"
              dataKey="budget"
              name="Budget"
              stroke={BUDGET_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <StaticLegend />
    </div>
  );
}

StackedTrendChart.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string,
      cloud: PropTypes.number,
      ai: PropTypes.number,
      budget: PropTypes.number,
      is_forecast: PropTypes.bool,
    })
  ),
};
